"use client";

import { useState } from "react";
import { useMutation } from "convex/react";
import { api } from "@convex/_generated/api";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { MapPin, Plus, X, Star } from "lucide-react";
import type { Settings } from "@convex/settings";

interface StationsSectionProps {
  settings: Settings;
}

export function StationsSection({ settings }: StationsSectionProps) {
  const [newStation, setNewStation] = useState("");
  const updateSettings = useMutation(api.settings.updateSettings);

  const handleAddStation = async () => {
    const trimmed = newStation.trim().toUpperCase();
    if (!trimmed || settings.stations.includes(trimmed)) return;

    await updateSettings({
      stations: [...settings.stations, trimmed],
    });
    setNewStation("");
  };

  const handleRemoveStation = async (station: string) => {
    const newStations = settings.stations.filter((s) => s !== station);

    // Clear default if removed station was the default
    if (settings.defaultStation === station) {
      await updateSettings({
        stations: newStations,
        defaultStation: null,
      });
    } else {
      await updateSettings({ stations: newStations });
    }
  };

  const handleSetDefault = async (station: string) => {
    await updateSettings({
      defaultStation: station === settings.defaultStation ? null : station,
    });
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <MapPin className="h-5 w-5 text-muted-foreground" />
          <CardTitle className="text-lg">Stations</CardTitle>
        </div>
        <CardDescription>Manage your delivery stations</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Add station input */}
        <div className="flex gap-2">
          <Input
            placeholder="Station code (e.g., DAX5)"
            value={newStation}
            onChange={(e) => setNewStation(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleAddStation()}
          />
          <Button type="button" onClick={handleAddStation} size="icon">
            <Plus className="h-4 w-4" />
          </Button>
        </div>

        {/* Station list */}
        <div className="space-y-2">
          {settings.stations.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No stations added yet
            </p>
          ) : (
            settings.stations.map((station) => (
              <div
                key={station}
                className="flex items-center justify-between p-2 border rounded-md"
              >
                <div className="flex items-center gap-2">
                  <span className="font-mono">{station}</span>
                  {station === settings.defaultStation && (
                    <Badge variant="secondary" className="text-xs">
                      Default
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => handleSetDefault(station)}
                    title={
                      station === settings.defaultStation
                        ? "Remove default"
                        : "Set as default"
                    }
                  >
                    <Star
                      className={`h-4 w-4 ${
                        station === settings.defaultStation
                          ? "fill-yellow-500 text-yellow-500"
                          : ""
                      }`}
                    />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => handleRemoveStation(station)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>
      </CardContent>
    </Card>
  );
}
